/**
 * ADMINISTRATION VOCABULARY → HEBREW.
 *
 * The same discipline as `denialText.ts`, applied to the administration surfaces: every code the
 * server can send has one Hebrew phrase, stated once, in a pure module a test can enumerate. There
 * is no `?? code` fallback that would put `required_for_admins` on a screen (09 §9.1 V3 — the ONLY
 * technical string a customer ever sees is a support reference code).
 *
 * `contract.mirror.test.ts` fails the build when `packages/contracts` or `packages/authn` grows a
 * member this file has no Hebrew for, in exactly the way it already does for `DENY_REASONS`. A
 * status with no Hebrew is an English enum on a Hebrew screen with extra steps.
 *
 * This module DECIDES NOTHING. It maps codes to words. It grants nothing, it hides nothing, and no
 * screen may branch on the tone it returns for anything but chrome (09 §9.5 A3: colour is never the
 * only carrier of meaning, so every tone is accompanied by the word itself).
 */

import type {
  AccessLevel,
  AuditActorKind,
  AuditOutcome,
  CapacityKind,
  CompanyLifecycleStatus,
  ConnectorStatus,
  MembershipStatus,
  PlatformRole,
  SeededTenantRole,
  SubscriptionState,
  TenantMfaPolicy,
  TenantStatus,
} from './contract';

/** Chrome only. A3: every use of a tone is accompanied by the Hebrew word it decorates. */
export type Tone = 'ok' | 'warn' | 'error' | 'muted';

export interface TermHe {
  readonly labelHe: string;
  readonly tone: Tone;
}

const TENANT_STATUS_HE: Readonly<Record<TenantStatus, TermHe>> = {
  active: { labelHe: 'פעיל', tone: 'ok' },
  trial: { labelHe: 'תקופת ניסיון', tone: 'warn' },
  suspended: { labelHe: 'מושהה', tone: 'error' },
  closed: { labelHe: 'סגור', tone: 'muted' },
};

export function tenantStatusHe(status: TenantStatus): TermHe {
  return TENANT_STATUS_HE[status];
}

/**
 * 08 §5.1 + §6.0. `draft` is not a state that grants anything, and the Hebrew says so rather than
 * implying a subscription that merely has not started yet.
 */
const SUBSCRIPTION_STATE_HE: Readonly<Record<SubscriptionState, TermHe>> = {
  draft: { labelHe: 'טיוטה — טרם הופעל', tone: 'muted' },
  trialing: { labelHe: 'תקופת ניסיון', tone: 'warn' },
  active: { labelHe: 'פעיל', tone: 'ok' },
  past_due: { labelHe: 'קיים חוב פתוח', tone: 'warn' },
  suspended: { labelHe: 'מושהה', tone: 'error' },
  cancelled: { labelHe: 'בוטל — פעיל עד תום התקופה', tone: 'warn' },
  expired: { labelHe: 'הסתיים', tone: 'error' },
};

export function subscriptionStateHe(state: SubscriptionState): TermHe {
  return SUBSCRIPTION_STATE_HE[state];
}

const MEMBERSHIP_STATUS_HE: Readonly<Record<MembershipStatus, TermHe>> = {
  invited: { labelHe: 'הוזמן — טרם הצטרף', tone: 'warn' },
  active: { labelHe: 'פעיל', tone: 'ok' },
  suspended: { labelHe: 'מושהה', tone: 'warn' },
  disabled: { labelHe: 'מושבת', tone: 'muted' },
};

export function membershipStatusHe(status: MembershipStatus): TermHe {
  return MEMBERSHIP_STATUS_HE[status];
}

/**
 * The eight company lifecycle states, verbatim from `packages/contracts/src/enums.ts`.
 *
 * 09 decision 10: a company's state is DISPLAYED, never inferred — `company_tags.json` held no
 * `active` key at all for 167 of 313 companies, and two code paths resolved that absence in opposite
 * directions. `<LifecycleChip>` renders these as chips and `lifecycleText.ts` puts them in sentences,
 * from this one map.
 */
const LIFECYCLE_STATUS_HE: Readonly<Record<CompanyLifecycleStatus, string>> = {
  prospect: 'לקוח פוטנציאלי',
  active: 'פעילה',
  dormant: 'רדומה',
  former_client: 'לקוח לשעבר',
  merged_into: 'מוזגה',
  quarantined: 'בהסגר',
  source_unavailable: 'מקור הנתונים אינו זמין',
  review_required: 'ממתינה לבדיקה',
};

export function lifecycleStatusHe(status: CompanyLifecycleStatus): string {
  return LIFECYCLE_STATUS_HE[status];
}

/** 01 §A5. Note the absence: there is no `deny` level, so there is no Hebrew for one. */
const ACCESS_LEVEL_HE: Readonly<Record<AccessLevel, string>> = {
  read: 'צפייה',
  write: 'עדכון',
  approve: 'אישור',
};

export function accessLevelHe(level: AccessLevel): string {
  return ACCESS_LEVEL_HE[level];
}

/**
 * 01 §A4 — the five tenant role codes a tenant-provisioning path creates per office. They are not
 * seeded by a migration and cannot be; the names below are what those codes will be CALLED.
 */
const TENANT_ROLE_HE: Readonly<Record<SeededTenantRole, string>> = {
  tenant_owner: 'בעלים של המשרד',
  tenant_admin: 'מנהל המשרד',
  tenant_member: 'עובד המשרד',
  tenant_billing_contact: 'איש קשר לחיובים',
  client_viewer: 'צפייה בלבד — לקוח',
};

export function tenantRoleHe(role: SeededTenantRole): string {
  return TENANT_ROLE_HE[role];
}

/** 04 §6.2 — the four platform roles. A disjoint namespace from the tenant roles above. */
const PLATFORM_ROLE_HE: Readonly<Record<PlatformRole, string>> = {
  platform_owner: 'בעלים של הפלטפורמה',
  platform_operator: 'מפעיל הפלטפורמה',
  platform_support: 'תמיכה',
  platform_auditor: 'מבקר',
};

export function platformRoleHe(role: PlatformRole): string {
  return PLATFORM_ROLE_HE[role];
}

const CAPACITY_KIND_HE: Readonly<Record<CapacityKind, { readonly nounHe: string; readonly unitHe: string }>> = {
  company: { nounHe: 'חברות', unitHe: 'חברות' },
  seat: { nounHe: 'משתמשים', unitHe: 'מושבים' },
  module_company: { nounHe: 'חברות במודול', unitHe: 'חברות' },
};

export function capacityKindHe(kind: CapacityKind): { readonly nounHe: string; readonly unitHe: string } {
  return CAPACITY_KIND_HE[kind];
}

/** 07 §2.3 — `status` is an enum, never an `is_deleted` flag, and only ACTIVE may serve data. */
const CONNECTOR_STATUS_HE: Readonly<Record<ConnectorStatus, TermHe>> = {
  PROVISIONING: { labelHe: 'בהקמה', tone: 'warn' },
  ACTIVE: { labelHe: 'פעיל', tone: 'ok' },
  SUSPENDED: { labelHe: 'מושהה', tone: 'warn' },
  FAILED: { labelHe: 'תקול', tone: 'error' },
  REVOKED: { labelHe: 'בוטל', tone: 'muted' },
};

export function connectorStatusHe(status: ConnectorStatus): TermHe {
  return CONNECTOR_STATUS_HE[status];
}

const AUDIT_OUTCOME_HE: Readonly<Record<AuditOutcome, TermHe>> = {
  allow: { labelHe: 'בוצע', tone: 'ok' },
  deny: { labelHe: 'נדחה', tone: 'error' },
};

export function auditOutcomeHe(outcome: AuditOutcome): TermHe {
  return AUDIT_OUTCOME_HE[outcome];
}

const AUDIT_ACTOR_KIND_HE: Readonly<Record<AuditActorKind, string>> = {
  user: 'משתמש',
  service: 'שירות',
  job: 'תהליך מתוזמן',
  anonymous: 'לא מזוהה',
  platform_support: 'תמיכת הפלטפורמה',
};

export function auditActorKindHe(kind: AuditActorKind): string {
  return AUDIT_ACTOR_KIND_HE[kind];
}

const MFA_POLICY_HE: Readonly<Record<TenantMfaPolicy, string>> = {
  optional: 'אימות דו-שלבי אינו חובה במשרד',
  required_for_admins: 'אימות דו-שלבי חובה למנהלי המשרד',
  required_for_all: 'אימות דו-שלבי חובה לכל משתמשי המשרד',
};

export function mfaPolicyHe(policy: TenantMfaPolicy): string {
  return MFA_POLICY_HE[policy];
}

/**
 * CAPABILITY → HEBREW, verbatim from `descriptionHe` in `packages/contracts/src/capabilities.ts`.
 *
 * Verbatim is the point: 04 §7.1 / 02 §5.1 make the CODE the declaration of what exists, and a
 * second Hebrew name invented here would be a second catalogue. The mirror test reads that file and
 * fails if a phrase drifts by one character.
 *
 * Only the TENANT set is listed. The tenant set is open by design — a module contributes its own
 * when it migrates — so `capabilityDescriptionHe` answers `null` for a code it has never seen, and
 * the roles screen writes that absence out rather than printing the code.
 */
const CAPABILITY_HE: Readonly<Record<string, string>> = {
  'companies.company.read': 'צפייה בחברות',
  'companies.company.manage': 'ניהול חברות',
  'companies.identifier.read': 'צפייה במזהים חיצוניים',
  'companies.identifier.manage': 'ניהול מזהים חיצוניים',
  'companies.conflict.read': 'צפייה בסתירות זהות',
  'companies.conflict.resolve': 'הכרעה בסתירת זהות',
  'companies.group.manage': 'ניהול קבוצות חברות',
  'companies.portfolio.manage': 'ניהול תיקי עבודה',
  'companies.company.create': 'הקמת חברה',
  'companies.assignment.manage': 'ניהול הרשאות לחברות',
  'tenant.user.manage': 'ניהול משתמשים',
  'tenant.role.manage': 'ניהול תפקידים',
  'tenant.settings.read': 'צפייה בהגדרות המשרד',
  'tenant.branding.manage': 'ניהול מיתוג המשרד',
  'tenant.user.invite': 'הזמנת משתמש',
  'tenant.settings.manage': 'ניהול הגדרות המשרד',
  'audit.read': 'צפייה ביומן ביקורת',
  'security.support_grant.read': 'צפייה בהרשאות תמיכה',
  'security.support_grant.revoke': 'ביטול הרשאת תמיכה',
  'billing.plan.read': 'צפייה בתכנית',
  'billing.usage.read': 'צפייה בשימוש',
  'billing.invoice.read': 'צפייה בחשבוניות',
  'billing.invoice.download': 'הורדת חשבונית',
  'billing.payment_method.update': 'עדכון אמצעי תשלום',
  'billing.profile.update': 'עדכון פרטי חיוב',
  'billing.change.request': 'בקשת שינוי מסחרי',
  'billing.subscription.cancel': 'ביטול מנוי',
  'billing.export.request': 'בקשת ייצוא נתונים',
};

/** `null` ⇒ this build has no Hebrew for that capability. The screen says so; it never prints it. */
export function capabilityDescriptionHe(code: string): string | null {
  return CAPABILITY_HE[code] ?? null;
}

/** Exposed for the mirror test only. */
export function describedCapabilityCodes(): readonly string[] {
  return Object.keys(CAPABILITY_HE);
}

/**
 * A UTC instant as an office date-time.
 *
 * UTC deliberately, and stated on screen as such: 07 §6.2's watermarks are UTC instants, and a
 * freshness line silently re-based to whatever timezone the browser happens to be in is a freshness
 * line two people cannot compare. 09 §9.5 A1: the digits run LTR under an explicit `dir`, never
 * left to the browser's bidi guess — that is the caller's job, and every call site does it.
 */
export function formatInstantHe(iso: string | null): string | null {
  if (iso === null) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const pad = (n: number): string => String(n).padStart(2, '0');
  const date = `${pad(at.getUTCDate())}/${pad(at.getUTCMonth() + 1)}/${at.getUTCFullYear()}`;
  return `${date} ${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}`;
}
